/*
 * WebCRD
 * Web to print solution that automates ordering, fulfillment, job ticketing, production management and chargebacks across corporate print centers.
 * Copyright 1999-2026 Rochester Software Associates (service@rocsoft.com)
 */

import { getLogger } from 'loglevel';
import PropTypes from 'prop-types';
import { useMemo } from 'react';

const logger = getLogger(Symbol('AutomationID'));

const ERROR_ON_INVALID_ID = false;

export const automationIDPropTypes = PropTypes.shape({
    id: PropTypes.string.isRequired,
});

/**
 * If you are creating an automationID in a component that might redraw, use this.
 * If you are creating an automationID inline in a prop passed to the component, use getAutomationID
 * Ex: <Component automation={getAutomationID(rootID, 'testString')} />
 * The reason being, when its inline, its scope is reduced to that component only. It won't be affected by a redraw of the parent component.
 * Also you can't have a hook inline anyway, because it's conditional.
 *
 * @returns {object} a memorized automation ID that will not change on re-render
 */
export const useMemoizedAutomationID = (rootID, element, databaseID) => {
    return useMemo(() => {
        return getAutomationID(rootID, element, databaseID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [databaseID, element, rootID?.role, rootID?.page]);
};

/**
 * Use if you are creating an automationID inline in a prop passed to the component.
 * Ex: <Component automation={getAutomationID(rootID, 'testString')} />
 *
 * @returns {object} an automation ID
 */
export const getAutomationID = (rootID, element, databaseID) => {
    let id;
    let mappingName;
    let idList;

    if (!rootID) {
        id = null;
        mappingName = null;
        idList = null;
    } else if (!rootID.role) {
        if (ERROR_ON_INVALID_ID) {
            logger.warn('Invalid ID found for [' + rootID + '] Make sure you are using the build() functions in RootAutomationID.js!');
            return null;
        }

        id = `${rootID}`;
        mappingName = `${rootID}`;
        idList = `${rootID}`;
    } else {
        const sanitizedRole = rootID.role.replace(/-/g, '_');
        const sanitizedPage = rootID.page.replace(/-/g, '_');
        const sanitizedElement = element.replace(/-/g, '_');

        const bracketedID = getBracketedID(databaseID);

        id = `${sanitizedRole}__${sanitizedPage}__${sanitizedElement}${bracketedID}`;

        mappingName = element.toLowerCase();
        idList = `${element}IDs`;
    }

    return {
        id,
        mappingName,
        idList,
        with: (databaseID) => ({ id: `${id}${getBracketedID(databaseID)}` }),
        rootID,
    };
};

const getBracketedID = (bracketedID) => {
    return bracketedID ? `[${bracketedID}]` : '';
};

/**
 * If you are creating an automationID in a component that might redraw, use this.
 * If you are creating an automationID inline in a prop passed to the component, use getExtraAutomationID
 * Ex: <Component automation={getExtraAutomationID(rootID, 'testString')} />
 * The reason being, when its inline, its scope is reduced to that component only. It won't be affected by a redraw of the parent component.
 * Also you can't have a hook inline anyway, because it's conditional.
 *
 * @returns {object} a memorized automation ID that will not change on re-render
 */
export const useMemoizedExtraAutomationID = (automationID, extra, databaseID) => {
    return useMemo(() => {
        return getExtraAutomationID(automationID, extra, databaseID);
    }, [automationID, extra, databaseID]);
};

export const getExtraAutomationID = (automationID, extra, databaseID) => {
    if (!automationID) {
        if (ERROR_ON_INVALID_ID) {
            logger.warn('Invalid extra ID found for [' + extra + '] Make sure you are using the getAutomationID() function!');
        }
    }
    const bracketedID = databaseID === undefined ? '' : `[${databaseID}]`;
    const extractedAutomationID = automationID?.id;

    const id = `${extractedAutomationID}__${extra}${bracketedID}`;
    return {
        id,
        idList: `${id}IDs`,
        rootID: automationID?.rootID,
    };
};